import { operators as o, common as c, PipelineData } from '@pendo/aggregations';
import { MESSAGE_TYPE_FILTERS } from '../components/ai-agents.constants';

export type MetadataFilterParams = {
    userReactionFilter?: string;
    fileUploadedFilter?: string;
    usedSuggestedPromptFilter?: string;
};

export type MetadataFiltersOptions = { skipFileFilter?: boolean; skipSuggestedPromptFilter?: boolean };

export function metadataFiltersStages (params: MetadataFilterParams | undefined, options?: MetadataFiltersOptions): PipelineData[] {
    if (!params) {
        return [];
    }
    const { fileUploadedFilter, usedSuggestedPromptFilter } = params;
    const stages: PipelineData[] = [];

    if (!options?.skipFileFilter) {
        if (fileUploadedFilter === 'yes') {
            stages.push(o.filter(`(${MESSAGE_TYPE_FILTERS.USER} && fileUploaded == true) || ${MESSAGE_TYPE_FILTERS.SYSTEM}`));
        } else if (fileUploadedFilter === 'no') {
            stages.push(o.filter(`(${MESSAGE_TYPE_FILTERS.USER} && (fileUploaded == false || isNull(fileUploaded))) || ${MESSAGE_TYPE_FILTERS.SYSTEM}`));
        }
    }

    if (!options?.skipSuggestedPromptFilter) {
        if (usedSuggestedPromptFilter === 'yes') {
            stages.push(o.filter(`(${MESSAGE_TYPE_FILTERS.USER} && suggestedPrompt) || ${MESSAGE_TYPE_FILTERS.SYSTEM}`));
        } else if (usedSuggestedPromptFilter === 'no') {
            stages.push(o.filter(`(${MESSAGE_TYPE_FILTERS.USER} && !suggestedPrompt) || ${MESSAGE_TYPE_FILTERS.SYSTEM}`));
        }
    }

    return stages;
}

export function fileUploadConversationScopedFilterStages (fileUploadedFilter: string | undefined, basePipeline: PipelineData[]): PipelineData[] {
    if (!fileUploadedFilter || (fileUploadedFilter !== 'yes' && fileUploadedFilter !== 'no')) {
        return [];
    }

    const condition = fileUploadedFilter === 'yes'
        ? '_convHasFileUpload > 0'
        : '_convHasFileUpload == 0 || isNull(_convHasFileUpload)';

    return [
        o.merge(
            ['conversationId'],
            o.mappings({ _convHasFileUpload: '_convHasFileUpload' }),
            o.pipeline(
                ...basePipeline,
                o.group(
                    ['conversationId'],
                    o.groupField('_convHasFileUpload', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && fileUploaded`)),
                ),
            ),
        ),
        o.filter(condition),
    ];
}

export function suggestedPromptConversationScopedFilterStages (usedSuggestedPromptFilter: string | undefined, basePipeline: PipelineData[]): PipelineData[] {
    if (!usedSuggestedPromptFilter || (usedSuggestedPromptFilter !== 'yes' && usedSuggestedPromptFilter !== 'no')) {
        return [];
    }

    const condition = usedSuggestedPromptFilter === 'yes'
        ? '_convHasSuggestedPrompt > 0'
        : '_convHasSuggestedPrompt == 0 || isNull(_convHasSuggestedPrompt)';

    return [
        o.evaluate({ _suggestedPromptFilterKey: 'if(!isEmpty(conversationId), conversationId, eventId)' }),
        o.merge(
            ['_suggestedPromptFilterKey'],
            o.mappings({ _convHasSuggestedPrompt: '_convHasSuggestedPrompt' }),
            o.pipeline(
                ...basePipeline,
                o.evaluate({ _suggestedPromptFilterKey: 'if(!isEmpty(conversationId), conversationId, eventId)' }),
                o.group(
                    ['_suggestedPromptFilterKey'],
                    o.groupField('_convHasSuggestedPrompt', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && suggestedPrompt`)),
                ),
            ),
        ),
        o.filter(condition),
    ];
}

export function userReactionConversationScopedFilterStages (userReactionFilter: string | undefined, basePipeline: PipelineData[]): PipelineData[] {
    if (!userReactionFilter || (userReactionFilter !== 'thumbsUp' && userReactionFilter !== 'thumbsDown' && userReactionFilter !== 'mixed')) {
        return [];
    }

    let condition: string;
    if (userReactionFilter === 'thumbsUp') {
        condition = '_convPositiveReactions > 0 && (_convNegativeReactions == 0 || isNull(_convNegativeReactions))';
    } else if (userReactionFilter === 'thumbsDown') {
        condition = '_convNegativeReactions > 0 && (_convPositiveReactions == 0 || isNull(_convPositiveReactions))';
    } else {
        condition = '_convPositiveReactions > 0 && _convNegativeReactions > 0';
    }

    return [
        o.evaluate({ _reactionFilterKey: 'if(!isEmpty(conversationId), conversationId, eventId)' }),
        o.merge(
            ['_reactionFilterKey'],
            o.mappings({ _convPositiveReactions: '_convPositiveReactions', _convNegativeReactions: '_convNegativeReactions' }),
            o.pipeline(
                ...basePipeline,
                o.evaluate({ _reactionFilterKey: 'if(!isEmpty(conversationId), conversationId, eventId)' }),
                o.filter(MESSAGE_TYPE_FILTERS.USER_REACTION),
                o.sort('browserTime', 'desc'),
                o.group(
                    ['_reactionFilterKey', 'messageId'],
                    o.groupField('_lastReactionContent', o.first('content')),
                ),
                o.group(
                    ['_reactionFilterKey'],
                    o.groupField('_convPositiveReactions', o.countIf(null, '_lastReactionContent == "positive"')),
                    o.groupField('_convNegativeReactions', o.countIf(null, '_lastReactionContent == "negative"')),
                ),
            ),
        ),
        o.filter(condition),
    ];
}

export function metadataFiltersStagesForConversationCount (params: MetadataFilterParams | undefined): PipelineData[] {
    if (!params) {
        return [];
    }
    const { fileUploadedFilter, usedSuggestedPromptFilter } = params;
    const stages: PipelineData[] = [];

    if (fileUploadedFilter === 'yes') {
        stages.push(o.filter(`${MESSAGE_TYPE_FILTERS.USER} && fileUploaded == true`));
    } else if (fileUploadedFilter === 'no') {
        stages.push(o.filter(`${MESSAGE_TYPE_FILTERS.USER} && (fileUploaded == false || isNull(fileUploaded))`));
    }

    if (usedSuggestedPromptFilter === 'yes') {
        stages.push(o.filter(`${MESSAGE_TYPE_FILTERS.USER} && suggestedPrompt`));
    } else if (usedSuggestedPromptFilter === 'no') {
        stages.push(o.filter(`${MESSAGE_TYPE_FILTERS.USER} && !suggestedPrompt`));
    }

    return stages;
}

export function noFileUploadConversationStages (): PipelineData[] {
    return [
        o.group(
            ['conversationId'],
            o.groupField('hasFileUpload', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && fileUploaded == true`)),
            o.groupField('numPromptsInConvo', o.countIf(null, 'contains(eventType, ":prompt")')),
            o.groupField('numRageInConvo', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && ragePrompt != "None" && ragePrompt != ""`)),
            o.groupField('visitorId', o.first('visitorId')),
            o.groupField('accountId', o.first('accountId')),
        ),
        o.filter('hasFileUpload == 0'),
    ];
}

export function fileUploadYesConversationStages (): PipelineData[] {
    return [
        o.group(
            ['conversationId'],
            o.groupField('hasFileUpload', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && fileUploaded == true`)),
            o.groupField('numPromptsInConvo', o.countIf(null, 'contains(eventType, ":prompt")')),
            o.groupField('numRageInConvo', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && ragePrompt != "None" && ragePrompt != ""`)),
            o.groupField('visitorId', o.first('visitorId')),
            o.groupField('accountId', o.first('accountId')),
        ),
        o.filter('hasFileUpload > 0 && !isEmpty(conversationId)'),
    ];
}

export function suggestedPromptYesConversationStages (): PipelineData[] {
    return [
        o.group(
            ['conversationId'],
            o.groupField('suggestedPromptCount', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && suggestedPrompt`)),
            o.groupField('numPromptsInConvo', o.countIf(null, 'contains(eventType, ":prompt")')),
            o.groupField('numRageInConvo', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && ragePrompt != "None" && ragePrompt != ""`)),
            o.groupField('visitorId', o.first('visitorId')),
            o.groupField('accountId', o.first('accountId')),
        ),
        o.filter('suggestedPromptCount > 0 && !isEmpty(conversationId)'),
    ];
}

export function suggestedPromptNoConversationStages (): PipelineData[] {
    return [
        o.group(
            ['conversationId'],
            o.groupField('suggestedPromptCount', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && suggestedPrompt`)),
            o.groupField('numPromptsInConvo', o.countIf(null, 'contains(eventType, ":prompt")')),
            o.groupField('numRageInConvo', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && ragePrompt != "None" && ragePrompt != ""`)),
            o.groupField('visitorId', o.first('visitorId')),
            o.groupField('accountId', o.first('accountId')),
        ),
        o.filter('suggestedPromptCount == 0'),
    ];
}

export function metadataFiltersConversationStages (params: MetadataFilterParams | undefined): PipelineData[] {
    if (!params) {
        return [];
    }
    const { fileUploadedFilter, usedSuggestedPromptFilter, userReactionFilter } = params;
    const hasFile = fileUploadedFilter === 'yes' || fileUploadedFilter === 'no';
    const hasSuggestedPrompt = usedSuggestedPromptFilter === 'yes' || usedSuggestedPromptFilter === 'no';
    const hasReaction = userReactionFilter === 'thumbsUp' || userReactionFilter === 'thumbsDown' || userReactionFilter === 'mixed';
    if (!hasFile && !hasSuggestedPrompt && !hasReaction) {
        return [];
    }
    const conditions: string[] = [];
    if (fileUploadedFilter === 'yes') {
        conditions.push('(hasFileUpload > 0 && !isEmpty(conversationId))');
    } else if (fileUploadedFilter === 'no') {
        conditions.push('hasFileUpload == 0');
    }
    if (usedSuggestedPromptFilter === 'yes') {
        conditions.push('(suggestedPromptCount > 0 && !isEmpty(conversationId))');
    } else if (usedSuggestedPromptFilter === 'no') {
        conditions.push('suggestedPromptCount == 0');
    }
    if (userReactionFilter === 'thumbsUp') {
        conditions.push('numPositiveReactions > 0 && numNegativeReactions == 0');
    } else if (userReactionFilter === 'thumbsDown') {
        conditions.push('numNegativeReactions > 0 && numPositiveReactions == 0');
    } else if (userReactionFilter === 'mixed') {
        conditions.push('numPositiveReactions > 0 && numNegativeReactions > 0');
    }
    if (conditions.length === 0) {
        return [];
    }

    return [
        o.group(
            ['conversationId'],
            o.groupField('hasFileUpload', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && fileUploaded == true`)),
            o.groupField('suggestedPromptCount', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && suggestedPrompt`)),
            o.groupField('numPositiveReactions', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER_REACTION} && content == "positive"`)),
            o.groupField('numNegativeReactions', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER_REACTION} && content == "negative"`)),
            o.groupField('numPromptsInConvo', o.countIf(null, 'contains(eventType, ":prompt")')),
            o.groupField('numRageInConvo', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && ragePrompt != "None" && ragePrompt != ""`)),
            o.groupField('visitorId', o.first('visitorId')),
            o.groupField('accountId', o.first('accountId')),
        ),
        o.filter(conditions.join(' && ')),
    ];
}

export function userReactionConversationStages (userReactionFilter: string): PipelineData[] {
    let reactionFilter: string | null = null;
    if (userReactionFilter === 'thumbsUp') {
        reactionFilter = 'numPositiveReactions > 0 && numNegativeReactions == 0';
    } else if (userReactionFilter === 'thumbsDown') {
        reactionFilter = 'numNegativeReactions > 0 && numPositiveReactions == 0';
    } else if (userReactionFilter === 'mixed') {
        reactionFilter = 'numPositiveReactions > 0 && numNegativeReactions > 0';
    }

    return [
        o.group(
            ['conversationId'],
            o.groupField('numPositiveReactions', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER_REACTION} && content == "positive"`)),
            o.groupField('numNegativeReactions', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER_REACTION} && content == "negative"`)),
            o.groupField('numPromptsInConvo', o.countIf(null, 'contains(eventType, ":prompt")')),
            o.groupField('numRageInConvo', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && ragePrompt != "None" && ragePrompt != ""`)),
            o.groupField('visitorId', o.first('visitorId')),
            o.groupField('accountId', o.first('accountId')),
        ),
        ...(reactionFilter ? [o.filter(reactionFilter)] : []),
    ];
}

function getReactionFilterExpression (userReactionFilter: string): string | null {
    if (userReactionFilter === 'thumbsUp') {
        return 'numPositiveReactions > 0 && numNegativeReactions == 0';
    }
    if (userReactionFilter === 'thumbsDown') {
        return 'numNegativeReactions > 0 && numPositiveReactions == 0';
    }
    if (userReactionFilter === 'mixed') {
        return 'numPositiveReactions > 0 && numNegativeReactions > 0';
    }

    return null;
}

export function fileUploadAndUserReactionConversationStages (fileUploadedFilter: string, userReactionFilter: string): PipelineData[] {
    const reactionFilter = getReactionFilterExpression(userReactionFilter);
    let fileFilter: string;
    if (fileUploadedFilter === 'yes') {
        fileFilter = 'hasFileUpload > 0 && !isEmpty(conversationId)';
    } else {
        fileFilter = 'hasFileUpload == 0';
    }
    const stages: PipelineData[] = [
        o.group(
            ['conversationId'],
            o.groupField('hasFileUpload', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && fileUploaded == true`)),
            o.groupField('numPositiveReactions', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER_REACTION} && content == "positive"`)),
            o.groupField('numNegativeReactions', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER_REACTION} && content == "negative"`)),
            o.groupField('numPromptsInConvo', o.countIf(null, 'contains(eventType, ":prompt")')),
            o.groupField('numRageInConvo', o.countIf(null, `${MESSAGE_TYPE_FILTERS.USER} && ragePrompt != "None" && ragePrompt != ""`)),
            o.groupField('visitorId', o.first('visitorId')),
            o.groupField('accountId', o.first('accountId')),
        ),
        o.filter(fileFilter),
    ];
    if (reactionFilter) {
        stages.push(o.filter(reactionFilter));
    }

    return stages;
}

export type Column = {
    prop: string;
    primaryKeys: string[];
    kind: string;
    functionName: string;
    fieldName: string;
    isDefault: boolean;
};

export function toolFilter (toolId: string) {
    return o.filter(`contains(toolsUsed, "${toolId}")`);
}

export function modelFilter (modelId: string) {
    return o.filter(`modelUsed == "${modelId}"`);
}

export function trackedUseCaseFilter (selectedTrackedUseCaseIds: string[]) {
    if (!selectedTrackedUseCaseIds?.length) {
        return null;
    }

    const trackedUseCaseFilterExpression = selectedTrackedUseCaseIds.reduce((expression, id, index) => {
        let addition = '';
        if (index > 0) {
            addition += ' || ';
        }
        addition += `contains(trackedUseCaseIds, "${id}")`;

        return expression + addition;
    }, '');

    return o.filter(trackedUseCaseFilterExpression);
}

export function adjustParamsForEveryoneSegments (segmentId: string | null, includeAnonymous: string, blacklist: c.BlacklistTypes) {
    let adjustedSegmentId: string | null = segmentId;
    let adjustedIncludeAnonymous = includeAnonymous;
    let adjustedBlacklist = blacklist;

    switch (segmentId) {
        case 'everyoneExcludingAnonymous':
            adjustedSegmentId = null;
            adjustedIncludeAnonymous = 'visitorId';
            break;
        case 'anonymousVisitorsOnly':
            adjustedSegmentId = null;
            adjustedIncludeAnonymous = '!visitorId';
            break;
        case 'blacklistOnly':
            adjustedSegmentId = null;
            adjustedBlacklist = 'only';
            break;
        case 'everyone':
            adjustedSegmentId = null;
            break;
    }

    return { adjustedSegmentId, adjustedIncludeAnonymous, adjustedBlacklist };
}

export function useCaseMetricsPipeline ({
    useCaseNameFields,
    isConversationsEnabled,
    timeSeries,
}: {
    useCaseNameFields: Array<string>;
    additionalGroupByFields?: Array<string>;
    isConversationsEnabled: boolean;
    timeSeries: c.TimeSeries
}) {
    return [
        o.fork(
            o.pipeline(
                o.group(
                    useCaseNameFields,
                    ...(isConversationsEnabled
                        ? [
                            o.groupField(
                                'numConversations',
                                o.countIf('conversationId', '!isEmpty(conversationId)'),
                            ),
                            o.groupField(
                                'numPromptsPromptsOnlyAgent',
                                o.countIf(null, 'isEmpty(conversationId)'),
                            ),
                            o.groupField(
                                'conversationIds',
                                o.listIf('conversationId', '!isEmpty(conversationId)'),
                            ),
                        ]
                        : []),
                    o.groupField('numVisitors', o.countIf('visitorId', '!isEmpty(visitorId)')),
                    o.groupField('numAccounts', o.countIf('accountId', '!isEmpty(accountId)')),
                    o.groupField('numPrompts', o.count(null)),
                    o.groupField('reportTitle', o.first('reportTitle')),
                    o.groupField('reportDescription', o.first('reportDescription')),
                    o.groupField('numRagePrompts', o.countIf(null, 'ragePrompt != "None" && ragePrompt != ""')),
                    o.groupField('eventIds', o.listIf('eventId', MESSAGE_TYPE_FILTERS.USER)),
                    o.groupField('ragePromptsPerConversation',
                        o.subGroup(
                            ['conversationId'],
                            o.groupField('numRagePromptsInConversation', o.countIf('eventId', 'ragePrompt != "None" && ragePrompt != ""')),
                        ),
                    ),
                ),
                o.evalExpression({
                    numConversations: 'numConversations + numPromptsPromptsOnlyAgent',
                    numRagePromptsOverNumPrompts: 'if(numPrompts > 0, numRagePrompts / numPrompts * 100, 0)',
                }),
            ),
            // Match the clustering-v2 cluster-row contract: produce ragePromptConversationIds so the cluster blade's
            // ragePrompt drill-in can pass valid conversationIds to AiAgentClusterRowV2Aggregation's issue path
            // (which filters agenticConversationEvents by conversationId).
            ...(isConversationsEnabled
                ? [
                    o.pipeline(
                        o.filter('ragePrompt != "None" && ragePrompt != ""'),
                        o.group(
                            useCaseNameFields,
                            o.groupField('ragePromptConversationIds', o.listUnique('conversationId')),
                        ),
                    ),
                ]
                : []),
            ...(timeSeries.count && timeSeries.count >= 14
                ? [
                    o.pipeline(
                        o.evaluate({
                            weekStart: 'startOfPeriod("weekly", browserTime)',
                        }),
                        o.group(
                            [...useCaseNameFields, 'visitorId', 'weekStart'],
                            o.groupField('visitsInWeek', o.count('browserTime')),
                        ),
                        o.group([...useCaseNameFields, 'visitorId'], o.groupField('uniqueWeeks', o.count('weekStart'))),
                        o.evaluate({
                            isRetained: 'uniqueWeeks >= 2',
                        }),
                        o.group(
                            useCaseNameFields,
                            o.groupField('totalVisitors', o.count('visitorId')),
                            o.groupField('retainedVisitors', o.countIf('visitorId', 'isRetained')),
                        ),
                        o.evaluate({
                            retentionRate: 'retainedVisitors / totalVisitors * 100',
                        }),
                    ),
                ]
                : []),
        ),
        o.join(useCaseNameFields),
    ];
}
